import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { User, Heart, LogOut, Settings, Music, ChevronDown } from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'
import { useNavigate } from 'react-router-dom'
import AuthModal from './AuthModal'

const UserButton = () => {
  const { user, signOut, favorites } = useMusic()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      await signOut()
      setShowDropdown(false)
      navigate('/')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const goToFavorites = () => {
    navigate('/favorites')
    setShowDropdown(false)
  }

  // 如果用户未登录，显示登录按钮
  if (!user) {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAuthModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-full hover:from-purple-600 hover:to-blue-700 transition-all duration-200 shadow-lg"
        >
          <User className="w-4 h-4" />
          <span className="hidden sm:inline">登录</span>
        </motion.button>

        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      </>
    )
  }

  // 用户已登录，显示用户下拉菜单
  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-3 px-4 py-2 bg-white bg-opacity-80 backdrop-blur-sm rounded-full shadow-lg border border-white border-opacity-20 hover:bg-opacity-90 transition-all duration-200"
      >
        {/* 用户头像 */}
        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </div>

        {/* 用户信息（桌面端显示） */}
        <div className="hidden sm:block text-left">
          <p className="text-sm font-medium text-gray-800">
            {user.user_metadata?.full_name || user.email?.split('@')[0] || '用户'}
          </p>
          <p className="text-xs text-gray-500">
            {favorites.length} 首收藏
          </p>
        </div>

        <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
      </motion.button>

      {/* 下拉菜单 */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50"
          >
            {/* 用户信息头部 */}
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="font-medium text-gray-800">
                {user.user_metadata?.full_name || '音乐爱好者'}
              </p>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>

            {/* 菜单项 */}
            <div className="py-2">
              <motion.button
                whileHover={{ backgroundColor: 'rgba(147, 51, 234, 0.1)' }}
                onClick={goToFavorites}
                className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:text-purple-600 transition-colors"
              >
                <Heart className="w-4 h-4" />
                <span>我的收藏</span>
                <span className="ml-auto text-xs text-gray-400">
                  {favorites.length}
                </span>
              </motion.button>

              <motion.button
                whileHover={{ backgroundColor: 'rgba(147, 51, 234, 0.1)' }}
                onClick={() => {
                  navigate('/')
                  setShowDropdown(false)
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:text-purple-600 transition-colors"
              >
                <Music className="w-4 h-4" />
                <span>音乐发现</span>
              </motion.button>

              {/* 分隔线 */}
              <div className="my-2 border-t border-gray-100"></div>

              <motion.button
                whileHover={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>退出登录</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 点击外部关闭下拉菜单 */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  )
}

export default UserButton
